import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Stethoscope, UserCheck, Laptop2 } from "lucide-react";
import { SITE, whatsappLink } from "@/lib/site";

const services = [
  {
    icon: Stethoscope,
    title: "Movement & Posture Analysis",
    duration: "60 min · Consultation",
    desc: "Comprehensive assessment focused on posture, mobility restrictions, muscular imbalances, movement quality, joint function, and pain triggers, building your personalized corrective strategy.",
    bullets: [
      "Posture & alignment screening",
      "Joint-by-joint mobility audit",
      "Personalized corrective plan",
    ],
    img: SITE.images.movementScreen,
    cta: "Book Analysis",
    msg: "Hi Anoop, I'd like to book a Movement & Posture Analysis.",
    featured: false,
  },
  {
    icon: UserCheck,
    title: "1:1 Corrective Training",
    duration: "In-Person · Dubai Mall",
    desc: "Premium personal training built around your assessment: corrective work, strength, fat loss, and pain-free performance.",
    bullets: [
      "Tailored programming each session",
      "Mobility, strength & body composition",
      "Train Strength & Fitness · Zabeel",
    ],
    img: SITE.images.corrective,
    cta: "Start 1:1 Training",
    msg: "Hi Anoop, I'd like to start 1:1 Corrective Training.",
    featured: true,
  },
  {
    icon: Laptop2,
    title: "Online Coaching",
    duration: "Remote · Anywhere",
    desc: "Programming, mobility plans, and check-ins delivered remotely, built on the same evidence-based framework.",
    bullets: [
      "Custom program & video library",
      "Weekly check-ins & adjustments",
      "Designed for sustainable progress",
    ],
    img: SITE.images.online,
    cta: "Apply for Online Coaching",
    msg: "Hi Anoop, I'm interested in Online Coaching.",
    featured: false,
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-20 sm:py-28 lg:py-36 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">Services</div>
            <h2 className="font-display text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-[-0.02em] text-white">
              Coaching built around <span className="text-gradient-blue">your body.</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md">
            Every engagement starts with an assessment. Consultation-first, results-second.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`service-card-${i}`}
              className={`relative rounded-3xl overflow-hidden group ${
                s.featured ? "lg:-translate-y-4" : ""
              }`}
            >
              <div className={`relative h-full rounded-3xl border ${
                s.featured ? "border-[#00E5FF]/30 bg-gradient-to-b from-[#001a2e] to-black" : "border-white/10 bg-black/40"
              } backdrop-blur-xl flex flex-col`}>
                {s.featured && (
                  <div className="absolute top-5 right-5 z-10 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.18em] bg-gradient-to-r from-[#0055FF] to-[#00E5FF] text-black">
                    Most Popular
                  </div>
                )}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center">
                      <s.icon className="w-5 h-5 text-[#00E5FF]" />
                    </div>
                    <div className="text-xs uppercase tracking-[0.22em] text-zinc-400">{s.duration}</div>
                  </div>
                  <h3 className="mt-4 font-display text-2xl font-bold text-white">{s.title}</h3>
                  <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
                  <ul className="mt-5 space-y-2.5">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm text-zinc-300">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#00E5FF] shrink-0"></span>
                        {b}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={whatsappLink(s.msg)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-cta-${i}`}
                    className={`mt-auto pt-8 inline-flex items-center justify-between gap-2 text-sm font-semibold ${
                      s.featured ? "text-[#00E5FF]" : "text-white"
                    } hover:gap-3 transition-all`}
                  >
                    {s.cta}
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-10 text-center text-sm text-zinc-500">
          Pricing is consultation-based and tailored after your assessment.
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-2.5" data-testid="services-internal-links">
          {[
            { name: "Personal Training in Dubai", path: "/personal-trainer-dubai" },
            { name: "Corrective Exercise", path: "/corrective-exercise-dubai" },
            { name: "Weight Loss Training", path: "/fat-loss-coach-dubai" },
            { name: "Strength & Conditioning", path: "/strength-conditioning-dubai" },
            { name: "Injury Prevention", path: "/injury-prevention-training-dubai" },
            { name: "Post Rehabilitation", path: "/post-rehabilitation-exercise-dubai" },
            { name: "Home Training", path: "/home-personal-trainer-dubai" },
            { name: "Online Coaching", path: "/online-personal-trainer-dubai" },
            { name: "Corporate Training", path: "/corporate-personal-training-dubai" },
            { name: "Small Group Training", path: "/small-group-personal-training-dubai" },
          ].map((l) => (
            <Link
              key={l.path}
              to={l.path}
              className="px-4 py-2 rounded-full border border-white/10 bg-white/[0.02] text-xs text-zinc-400 hover:text-white hover:border-[#00E5FF]/40 transition-colors"
            >
              {l.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
